import { useEffect, useRef, useState } from 'react';
import type { LobbyState, WSClientMsg, WSServerMsg } from '@geoguess/shared';
import { type AuthedUser, initDiscord } from './discord.js';
import { connect, type WSConnection } from './ws.js';
import { LobbyScreen } from './screens/LobbyScreen.js';
import { RoundScreen } from './screens/RoundScreen.js';
import { RevealScreen } from './screens/RevealScreen.js';
import { FinalScreen } from './screens/FinalScreen.js';
import { LoadingScreen } from './screens/LoadingScreen.js';

type Phase = 'init' | 'connecting' | 'live' | 'error';

export function App() {
  const [phase, setPhase] = useState<Phase>('init');
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<AuthedUser | null>(null);
  const [lobby, setLobby] = useState<LobbyState | null>(null);
  const [lastMsg, setLastMsg] = useState<WSServerMsg | null>(null);
  const wsRef = useRef<WSConnection | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await initDiscord();
        if (cancelled) return;
        setUser(u);
        setPhase('connecting');
        const conn = connect(
          { t: 'hello', userId: u.userId, name: u.name, avatar: u.avatar, lobbyId: u.lobbyId },
          {
            onState: (l) => {
              setLobby(l);
              setPhase('live');
            },
            onMessage: (m) => setLastMsg(m),
            onClose: () => {
              // reconnect handled inside ws.ts
            },
          },
        );
        wsRef.current = conn;
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'unknown');
        setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
      wsRef.current?.close();
    };
  }, []);

  const send = (msg: WSClientMsg) => wsRef.current?.send(msg);

  if (phase === 'init' || phase === 'connecting' || !user) {
    return <LoadingScreen text={phase === 'init' ? 'Anmeldung…' : 'Verbinden…'} />;
  }
  if (phase === 'error') {
    return (
      <div className="bg-hero grid h-full place-items-center p-8 text-center">
        <div className="anim-slide-up max-w-md rounded-2xl bg-panel p-6 ring-1 ring-border">
          <h1 className="text-xl font-bold text-danger">Konnte nicht starten</h1>
          <p className="mt-3 text-sm text-muted">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="press mt-5 inline-flex items-center justify-center rounded-2xl bg-accent px-5 py-2.5 font-semibold text-white shadow-[0_8px_24px_rgba(10,132,255,0.35)] transition hover:brightness-110"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }
  if (!lobby) return <LoadingScreen text="Trete Lobby bei…" />;

  const isHost = lobby.hostUserId === user.userId;
  switch (lobby.phase) {
    case 'lobby':
      return <LobbyScreen lobby={lobby} me={user} isHost={isHost} send={send} />;
    case 'round':
      return <RoundScreen lobby={lobby} me={user} send={send} />;
    case 'reveal':
      return <RevealScreen lobby={lobby} me={user} lastReveal={lastMsg} />;
    case 'final':
      return <FinalScreen lobby={lobby} me={user} isHost={isHost} send={send} />;
  }
}
